/**
 * The Map Controls container for the default map: a small card pinned to the
 * far bottom-left that is ALWAYS visible, showing the active layer's compact
 * legend swatches plus a "Map Controls" button. Pressing the button reveals
 * the rest (product / model run / color-by selects, the 3D view, the layer
 * visibility/opacity/swipe controls, and the basemap / region / structures
 * footer) in a panel that expands UPWARD from the card and is not draggable,
 * with a close affordance.
 *
 * Dark frosted glass + amber accent, matching the layer-controls styling.
 */
import React from 'react';
import { Box, Collapse, IconButton, Tooltip, Typography } from '@mui/material';
import LayersIcon from '@mui/icons-material/Layers';
import CloseIcon from '@mui/icons-material/Close';
import { ACCENT, ACCENT_HOVER, DARK_BLUE, PANEL_BG } from './palette';

const PANEL_ID = 'map-controls-panel';

type Props = {
  /** Compact active-layer legend, always visible in the card. */
  legend: React.ReactNode;
  /** The revealed controls: model select + the layer controls. */
  controls: React.ReactNode;
  /** The revealed footer: basemap / region view / structures. */
  footer: React.ReactNode;
};

const frostedSx = {
  backgroundColor: PANEL_BG,
  backdropFilter: 'blur(8px)',
  border: '1px solid rgba(255,255,255,0.18)',
  borderRadius: '8px',
  color: '#fff',
};

export const BottomLeftPanel: React.FC<Props> = ({ legend, controls, footer }) => {
  const [open, setOpen] = React.useState(false);

  return (
    // The stack spans the whole map container so the panel + card can never
    // outgrow it: the card keeps its natural height and the revealed panel
    // flexes into whatever space remains above it, scrolling internally.
    // pointer-events pass through the empty area to the map; only the
    // rendered children catch them.
    <Box
      sx={{
        position: 'absolute',
        left: 8,
        top: 8,
        bottom: 8,
        zIndex: 1000,
        pointerEvents: 'none',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        gap: 1,
        maxWidth: 320,
        '@media (max-width: 1200px)': {
          transform: 'scale(0.9)',
          transformOrigin: 'bottom left',
        },
      }}
    >
      {/* Revealed panel -- expands upward, above the always-visible card.
          Once the enter transition settles, the flex column may shrink the
          Collapse below its content height; the 100% chain hands that
          constraint down so the panel body scrolls instead of overflowing
          off the top of the map. */}
      <Collapse
        in={open}
        unmountOnExit
        sx={{
          minHeight: 0,
          pointerEvents: 'auto',
          '&.MuiCollapse-entered': {
            overflow: 'hidden',
            '& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner': { height: '100%' },
          },
        }}
      >
        <Box
          id={PANEL_ID}
          sx={{
            ...frostedSx,
            width: 300,
            maxHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1,
              py: 0.5,
              borderBottom: '1px solid rgba(255,255,255,0.15)',
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                color: '#fff',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <LayersIcon fontSize="small" /> Map Controls
            </Typography>
            <Tooltip title="Close">
              <IconButton
                size="small"
                aria-label="Close map controls"
                onClick={() => setOpen(false)}
                sx={{ color: '#fff', '&:focus-visible': { outline: `2px solid ${ACCENT}` } }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 1 }}>
            {controls}
            <Box sx={{ mt: 1, borderTop: '1px solid rgba(255,255,255,0.15)', pt: 1 }}>{footer}</Box>
          </Box>
        </Box>
      </Collapse>

      {/* Always-visible card: compact legend + the reveal button. The card
          never shrinks -- the panel above absorbs the squeeze -- and the
          legend cap is viewport-relative because the legends bring their own
          scroll regions; a fixed small cap would nest two scrollbars. */}
      <Box
        sx={{
          ...frostedSx,
          p: 1,
          width: 'fit-content',
          maxWidth: 300,
          flexShrink: 0,
          pointerEvents: 'auto',
        }}
      >
        <Box sx={{ maxHeight: '45vh', overflow: 'auto', mb: 1 }}>{legend}</Box>
        <Tooltip
          title="Product, model run, color-by, 3D view, layer, basemap and region controls"
          describeChild
        >
          <IconButton
            aria-label={open ? 'Hide map controls' : 'Show map controls'}
            aria-expanded={open}
            aria-controls={open ? PANEL_ID : undefined}
            onClick={() => setOpen(o => !o)}
            sx={{
              width: '100%',
              borderRadius: '6px',
              gap: 0.75,
              py: 0.5,
              fontSize: 14,
              fontWeight: 700,
              color: open ? DARK_BLUE : '#fff',
              backgroundColor: open ? ACCENT : 'transparent',
              border: `1.5px solid ${open ? ACCENT : '#fff'}`,
              '&:hover': { backgroundColor: open ? ACCENT_HOVER : 'rgba(255,255,255,0.12)' },
              '&:focus-visible': { outline: `2px solid ${ACCENT}` },
            }}
          >
            <LayersIcon fontSize="small" />
            Map Controls
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};
